"use client";

/**
 * ChatbotItem Component
 *
 * Individual chatbot item for the sidebar list.
 * Shows chatbot name with .chatbot extension, model provider indicator, and action buttons.
 * Supports drag-and-drop for organization.
 */
import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Chatbot } from "@/types/chatbot";
type ChatbotItemProps = {
  /** The chatbot to display */
  chatbot: Chatbot;
  /** Whether this chatbot is selected */
  isSelected: boolean;
  /** Called when the chatbot is clicked */
  onClick: () => void;
  /** Called when starting a new thread with this chatbot */
  onStartThread?: () => void;
  /** Called when the edit settings action is triggered */
  onEdit?: () => void;
  /** Called when the duplicate action is triggered */
  onDuplicate?: () => void;
  /** Called when the delete action is triggered */
  onDelete?: () => void;
  /** Called when the name is updated inline */
  onRename?: (newName: string) => void;
  /** Indentation depth */
  depth?: number;
  /** Whether this item is draggable */
  isDraggable?: boolean;
  /** Additional class names */
  className?: string;
};
export function ChatbotItem(t0) {
  const $ = _c(61);
  const {
    chatbot,
    isSelected,
    onClick,
    onStartThread,
    onEdit,
    onDuplicate,
    onDelete,
    onRename,
    depth: t1,
    isDraggable: t2,
    className: t3
  } = t0;
  const depth = t1 === undefined ? 0 : t1;
  const isDraggable = t2 === undefined ? false : t2;
  const className = t3 === undefined ? "" : t3;
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(chatbot.name);
  const inputRef = useRef(null);
  let t4;
  if ($[0] !== chatbot) {
    t4 = {
      type: "chatbot",
      chatbot
    };
    $[0] = chatbot;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = !isDraggable;
  let t6;
  if ($[2] !== chatbot.id || $[3] !== t4 || $[4] !== t5) {
    t6 = {
      id: chatbot.id,
      data: t4,
      disabled: t5
    };
    $[2] = chatbot.id;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t6);
  let t7;
  if ($[6] !== isDraggable || $[7] !== isDragging || $[8] !== transform || $[9] !== transition) {
    t7 = isDraggable ? {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1
    } : undefined;
    $[6] = isDraggable;
    $[7] = isDragging;
    $[8] = transform;
    $[9] = transition;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const style = t7;
  let t8;
  let t9;
  if ($[11] !== isEditing) {
    t8 = () => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    };
    t9 = [isEditing];
    $[11] = isEditing;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] !== chatbot.name || $[15] !== editedName || $[16] !== onRename) {
    t10 = () => {
      const trimmed = editedName.trim();
      if (trimmed && trimmed !== chatbot.name && onRename) {
        onRename(trimmed);
      }
      setIsEditing(false);
      setEditedName(chatbot.name);
    };
    $[14] = chatbot.name;
    $[15] = editedName;
    $[16] = onRename;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleSaveName = t10;
  let t11;
  if ($[18] !== chatbot.name || $[19] !== handleSaveName) {
    t11 = e => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSaveName();
      } else {
        if (e.key === "Escape") {
          setIsEditing(false);
          setEditedName(chatbot.name);
        }
      }
    };
    $[18] = chatbot.name;
    $[19] = handleSaveName;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handleKeyDown = t11;
  let t12;
  if ($[21] !== chatbot.name) {
    t12 = e_0 => {
      e_0.stopPropagation();
      setEditedName(chatbot.name);
      setIsEditing(true);
    };
    $[21] = chatbot.name;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const handleStartEdit = t12;
  let t13;
  if ($[23] !== onStartThread) {
    t13 = e_1 => {
      e_1.stopPropagation();
      if (onStartThread) {
        onStartThread();
      }
    };
    $[23] = onStartThread;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const handleStartThread = t13;
  let t14;
  if ($[25] !== onEdit) {
    t14 = e_2 => {
      e_2.stopPropagation();
      if (onEdit) {
        onEdit();
      }
    };
    $[25] = onEdit;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const handleEdit = t14;
  let t15;
  if ($[27] !== onDuplicate) {
    t15 = e_3 => {
      e_3.stopPropagation();
      if (onDuplicate) {
        onDuplicate();
      }
    };
    $[27] = onDuplicate;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const handleDuplicate = t15;
  let t16;
  if ($[29] !== chatbot.name || $[30] !== onDelete) {
    t16 = e_4 => {
      e_4.stopPropagation();
      const confirmed = window.confirm(`Are you sure you want to delete "${chatbot.name}.chatbot"?\n\nThis will remove the chatbot configuration. Threads using this chatbot will switch to default settings.`);
      if (confirmed && onDelete) {
        onDelete();
      }
    };
    $[29] = chatbot.name;
    $[30] = onDelete;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const handleDelete = t16;
  const provider = chatbot.config?.model?.provider || "ai";
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      openai: "bg-green-500",
      claude: "bg-orange-500",
      grok: "bg-blue-500",
      gemini: "bg-purple-500"
    };
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const providerColors = t17;
  const dotColor = providerColors[provider] || "bg-foreground/50";
  const paddingLeft = depth * 12 + 24;
  let t18;
  if ($[33] !== attributes || $[34] !== chatbot.name || $[35] !== dotColor || $[36] !== editedName || $[37] !== handleDelete || $[38] !== handleDuplicate || $[39] !== handleEdit || $[40] !== handleKeyDown || $[41] !== handleSaveName || $[42] !== handleStartEdit || $[43] !== handleStartThread || $[44] !== isDraggable || $[45] !== isEditing || $[46] !== isSelected || $[47] !== listeners || $[48] !== onClick || $[49] !== onDelete || $[50] !== onDuplicate || $[51] !== onEdit || $[52] !== onRename || $[53] !== onStartThread || $[54] !== paddingLeft) {
    t18 = isEditing ? <div className="flex items-center gap-1 px-2 py-1.5" style={{
      paddingLeft
    }}><input ref={inputRef} type="text" value={editedName} onChange={e_5 => setEditedName(e_5.target.value)} onKeyDown={handleKeyDown} onBlur={handleSaveName} onClick={_temp} className="flex-1 rounded border border-white/40 bg-white/60 px-2 py-1 text-sm text-foreground focus:border-sky focus:outline-none focus:ring-1 focus:ring-sky" /><span className="text-xs text-foreground/50">.chatbot</span></div> : <div {...isDraggable ? {
      ...attributes,
      ...listeners
    } : {}} onClick={onClick} className={`group relative flex items-center rounded-md px-2 py-1.5 transition-colors ${isDraggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${isSelected ? "bg-white/60 text-foreground font-medium" : "text-foreground hover:bg-white/40"}`} style={{
      paddingLeft
    }}><span className={`h-2 w-2 rounded-full flex-shrink-0 mr-2 ${dotColor}`} /><span className="flex-1 truncate text-sm">{chatbot.name}<span className="text-foreground/50">.chatbot</span></span><div className="absolute right-2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">{onStartThread && <button type="button" onClick={handleStartThread} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-sky" title="Start new thread with this chatbot"><svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg></button>}{onEdit && <button type="button" onClick={handleEdit} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-butter" title="Edit chatbot settings"><svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg></button>}{onDuplicate && <button type="button" onClick={handleDuplicate} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-lavender" title="Duplicate chatbot"><svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg></button>}{onRename && <button type="button" onClick={handleStartEdit} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-sky" title="Rename chatbot"><svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg></button>}{onDelete && <button type="button" onClick={handleDelete} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-red-400" title="Delete chatbot"><svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg></button>}</div></div>;
    $[33] = attributes;
    $[34] = chatbot.name;
    $[35] = dotColor;
    $[36] = editedName;
    $[37] = handleDelete;
    $[38] = handleDuplicate;
    $[39] = handleEdit;
    $[40] = handleKeyDown;
    $[41] = handleSaveName;
    $[42] = handleStartEdit;
    $[43] = handleStartThread;
    $[44] = isDraggable;
    $[45] = isEditing;
    $[46] = isSelected;
    $[47] = listeners;
    $[48] = onClick;
    $[49] = onDelete;
    $[50] = onDuplicate;
    $[51] = onEdit;
    $[52] = onRename;
    $[53] = onStartThread;
    $[54] = paddingLeft;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== className || $[57] !== setNodeRef || $[58] !== style || $[59] !== t18) {
    t19 = <div ref={setNodeRef} style={style} className={className}>{t18}</div>;
    $[56] = className;
    $[57] = setNodeRef;
    $[58] = style;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  return t19;
}
function _temp(e_6) {
  return e_6.stopPropagation();
}